import React, { useState, useEffect, useCallback } from "react";
import {
  getFeeStructuresByYear,
  getFeeStructuresByYearPortal,
  saveFeeStructure,
  saveFeeStructurePortal,
  getAllFeeHeads,
  getAllFeeHeadsPortal
} from "../../api/fees/feeApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import {
  getAllClasses
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal
} from "../../api/student/studentPortalApi";
import {
  useToast
} from "../../context/ToastContext";

/**
 * Row state per fee head:
 * - mode "SAME": one amount applied to every selected class (e.g. ID card)
 * - mode "PER_CLASS": each class has its own amount (e.g. tuition)
 */
const emptyRow = () => ({
  mode: "SAME",
  amount: "",
  dueDay: 10,
  classIds: [],
  perClass: {}, // { [classId]: amountString }
});

const isTransportHead = (h) =>
  h?.type === "TRANSPORT" || h?.source === "TRANSPORT_MODULE";

const FeeStructurePage = ({ isPortal = false }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [academicYearId, setAcademicYearId] = useState("");
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      try {
        const [ayRes, classData, headsRes] = await Promise.all([
          isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears(),
          isPortal ? getAllClassesForPortal() : getAllClasses(),
          isPortal
            ? getAllFeeHeadsPortal({ isActive: true })
            : getAllFeeHeads({ isActive: true }),
        ]);
        const years = ayRes?.data || ayRes || [];
        setAcademicYears(Array.isArray(years) ? years : []);
        setClasses(classData || []);
        const heads = headsRes?.data || headsRes || [];
        const structureHeads = (Array.isArray(heads) ? heads : []).filter(
          (h) => !isTransportHead(h)
        );
        setFeeHeads(structureHeads);
        const active = (Array.isArray(years) ? years : []).find((y) => y.isActive);
        if (active) setAcademicYearId(active._id);
      } catch {
        toast.error("Failed to load master data.");
      }
    };
    load();
  }, [isPortal]);

  const loadAllStructures = useCallback(async () => {
    if (!academicYearId || feeHeads.length === 0) return;

    setLoading(true);
    try {
      const res = isPortal
        ? await getFeeStructuresByYearPortal({ academicYearId })
        : await getFeeStructuresByYear({ academicYearId });
      const list = res?.data || res || [];

      const nextRows = {};
      feeHeads.forEach((h) => {
        nextRows[h._id] = emptyRow();
      });

      // Group active structures by fee head
      const byHead = {};
      list.forEach((s) => {
        const hid = String(s.feeHeadId?._id || s.feeHeadId);
        const classId = String(s.classId?._id || s.classId);
        if (!nextRows[hid]) return;
        if (!byHead[hid]) byHead[hid] = [];
        byHead[hid].push({
          classId,
          amount: s.amount,
          dueDay: s.dueDateDay ?? 10,
        });
      });

      Object.entries(byHead).forEach(([hid, entries]) => {
        const amounts = entries.map((e) => Number(e.amount));
        const allSame = amounts.every((a) => a === amounts[0]);
        const dueDay = entries[0]?.dueDay ?? 10;

        if (allSame) {
          nextRows[hid] = {
            mode: "SAME",
            amount: amounts[0] ?? "",
            dueDay,
            classIds: entries.map((e) => e.classId),
            perClass: {},
          };
        } else {
          const perClass = {};
          entries.forEach((e) => {
            perClass[e.classId] = e.amount;
          });
          nextRows[hid] = {
            mode: "PER_CLASS",
            amount: "",
            dueDay,
            classIds: entries.map((e) => e.classId),
            perClass,
          };
        }
      });

      setRows(nextRows);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load structures.");
    } finally {
      setLoading(false);
    }
  }, [academicYearId, feeHeads, isPortal]);

  useEffect(() => {
    loadAllStructures();
  }, [loadAllStructures]);

  const updateRow = (feeHeadId, patch) => {
    setRows((prev) => ({
      ...prev,
      [feeHeadId]: { ...(prev[feeHeadId] || emptyRow()), ...patch },
    }));
  };

  const setMode = (feeHeadId, mode) => {
    setRows((prev) => {
      const row = prev[feeHeadId] || emptyRow();
      if (mode === "PER_CLASS") {
        // Seed per-class amounts from shared amount for currently selected classes
        const perClass = { ...row.perClass };
        row.classIds.forEach((cid) => {
          if (perClass[cid] === undefined || perClass[cid] === "") {
            perClass[cid] = row.amount !== "" ? row.amount : "";
          }
        });
        return {
          ...prev,
          [feeHeadId]: { ...row, mode: "PER_CLASS", perClass },
        };
      }
      // SAME: if per-class values exist and match, keep first; else keep row.amount
      const vals = row.classIds
        .map((cid) => row.perClass?.[cid])
        .filter((v) => v !== undefined && v !== "");
      const amount =
        vals.length && vals.every((v) => Number(v) === Number(vals[0]))
          ? vals[0]
          : row.amount;
      return {
        ...prev,
        [feeHeadId]: { ...row, mode: "SAME", amount },
      };
    });
  };

  const toggleClassForRow = (feeHeadId, classId) => {
    setRows((prev) => {
      const row = prev[feeHeadId] || emptyRow();
      const active = row.classIds.map(String).includes(String(classId));
      const classIds = active
        ? row.classIds.filter((id) => String(id) !== String(classId))
        : [...row.classIds, classId];

      const perClass = { ...row.perClass };
      if (!active && row.mode === "PER_CLASS") {
        if (perClass[classId] === undefined) {
          perClass[classId] = row.amount !== "" ? row.amount : "";
        }
      }
      if (active) {
        delete perClass[classId];
      }

      return { ...prev, [feeHeadId]: { ...row, classIds, perClass } };
    });
  };

  const toggleSelectAllForRow = (feeHeadId) => {
    setRows((prev) => {
      const row = prev[feeHeadId] || emptyRow();
      const allSelected =
        row.classIds.length === classes.length && classes.length > 0;
      if (allSelected) {
        return {
          ...prev,
          [feeHeadId]: { ...row, classIds: [], perClass: {} },
        };
      }
      const classIds = classes.map((c) => c._id);
      const perClass = { ...row.perClass };
      if (row.mode === "PER_CLASS") {
        classIds.forEach((cid) => {
          if (perClass[cid] === undefined) {
            perClass[cid] = row.amount !== "" ? row.amount : "";
          }
        });
      }
      return {
        ...prev,
        [feeHeadId]: { ...row, classIds, perClass },
      };
    });
  };

  const setPerClassAmount = (feeHeadId, classId, value) => {
    setRows((prev) => {
      const row = prev[feeHeadId] || emptyRow();
      return {
        ...prev,
        [feeHeadId]: {
          ...row,
          perClass: { ...row.perClass, [classId]: value },
        },
      };
    });
  };

  const handleSaveAll = async () => {
    if (!academicYearId) {
      toast.error("Select an academic year.");
      return;
    }

    setSaving(true);
    try {
      // Expand to backend shape: one { feeHeadId, amount, dueDateDay, classIds } per amount group
      const structures = [];

      for (const h of feeHeads) {
        const row = rows[h._id] || emptyRow();
        const dueDateDay = Number(row.dueDay || 10);

        if (row.mode === "SAME") {
          if (!row.classIds.length) continue;
          structures.push({
            feeHeadId: h._id,
            amount: Number(row.amount || 0),
            dueDateDay,
            classIds: row.classIds,
          });
        } else {
          // PER_CLASS: group classes that share the same numeric amount
          const byAmount = {};
          row.classIds.forEach((cid) => {
            const raw = row.perClass?.[cid];
            if (raw === undefined || raw === "") return;
            const key = String(Number(raw));
            if (!byAmount[key]) byAmount[key] = [];
            byAmount[key].push(cid);
          });
          Object.entries(byAmount).forEach(([amt, classIds]) => {
            structures.push({
              feeHeadId: h._id,
              amount: Number(amt),
              dueDateDay,
              classIds,
            });
          });
        }
      }

      if (!structures.length) {
        toast.error(
          "Nothing to save. Select at least one class and enter an amount for a fee head."
        );
        setSaving(false);
        return;
      }

      // Heads with no classes selected must still clear previous assignments:
      // send empty classIds so backend deactivates them.
      feeHeads.forEach((h) => {
        const row = rows[h._id] || emptyRow();
        const already = structures.some(
          (s) => String(s.feeHeadId) === String(h._id)
        );
        if (!already && row.classIds.length === 0) {
          structures.push({
            feeHeadId: h._id,
            amount: 0,
            dueDateDay: Number(row.dueDay || 10),
            classIds: [],
          });
        }
      });

      const payload = { academicYearId, structures };

      if (isPortal) await saveFeeStructurePortal(payload);
      else await saveFeeStructure(payload);

      toast.success("Fee structures saved successfully.");
      loadAllStructures();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-1">Fee Structures</h1>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
        Set amount(s) and due day for each fee head, then choose which classes
        they apply to.
      </p>
      <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
        <strong>Same amount</strong> — one value for all selected classes (e.g.
        ID card).{" "}
        <strong>Per class</strong> — different amount for each class (e.g.
        tuition).{" "}
        <strong>Transport</strong> is not listed; it is billed from student
        transport assignments when you generate a batch.
      </div>

      
      

      <div className="mb-6">
        <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
          Academic Year
        </label>
        <select
          value={academicYearId}
          onChange={(e) => setAcademicYearId(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
        >
          <option value="">Academic Year</option>
          {academicYears.map((y) => (
            <option key={y._id} value={y._id}>
              {y.yearName} {y.isActive ? "(Active)" : ""}
            </option>
          ))}
        </select>
      </div>

      {!academicYearId ? (
        <div className="p-8 text-center text-neutral-500 dark:text-neutral-400 border rounded-lg">
          Select an academic year
        </div>
      ) : loading ? (
        <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">Loading...</div>
      ) : (
        <>
          <div className="space-y-4">
            {feeHeads.map((h) => {
              const row = rows[h._id] || emptyRow();
              const allSelected =
                row.classIds.length === classes.length && classes.length > 0;
              const isPerClass = row.mode === "PER_CLASS";

              return (
                <div
                  key={h._id}
                  className="bg-white dark:bg-neutral-900 border rounded-lg shadow-sm p-4"
                >
                  <div className="flex flex-wrap items-end gap-4 mb-3">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-medium">{h.name}</div>
                      <div className="text-xs text-neutral-400 dark:text-neutral-500">
                        {h.code} · {(h.type || "").replace(/_/g, " ")}
                      </div>
                    </div>

                    {/* Mode toggle */}
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                        Amount mode
                      </label>
                      <div className="inline-flex rounded-md border overflow-hidden text-sm">
                        <button
                          type="button"
                          onClick={() => setMode(h._id, "SAME")}
                          className={`px-3 py-1.5 ${ !isPerClass ? "bg-blue-600 text-white" : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800" }`}
                        >
                          Same for all
                        </button>
                        <button
                          type="button"
                          onClick={() => setMode(h._id, "PER_CLASS")}
                          className={`px-3 py-1.5 border-l ${ isPerClass ? "bg-blue-600 text-white" : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800" }`}
                        >
                          Per class
                        </button>
                      </div>
                    </div>

                    {!isPerClass && (
                      <div>
                        <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                          Amount
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.amount ?? ""}
                          onChange={(e) =>
                            updateRow(h._id, { amount: e.target.value })
                          }
                          className="w-28 border rounded px-2 py-1 text-right"
                          placeholder="0.00"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                        Due Day
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={row.dueDay ?? 10}
                        onChange={(e) =>
                          updateRow(h._id, { dueDay: e.target.value })
                        }
                        className="w-16 border rounded px-2 py-1 text-right"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                      {isPerClass
                        ? "Classes & amounts"
                        : "Assigned classes"}
                    </label>

                    {!isPerClass ? (
                      <>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => toggleSelectAllForRow(h._id)}
                            className={`px-3 py-1 rounded-full border text-sm transition ${ allSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-neutral-900 hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800" }`}
                          >
                            Select All
                          </button>
                          {classes.map((cls) => {
                            const active = row.classIds
                              .map(String)
                              .includes(String(cls._id));
                            return (
                              <button
                                key={cls._id}
                                type="button"
                                onClick={() =>
                                  toggleClassForRow(h._id, cls._id)
                                }
                                className={`px-3 py-1 rounded-full border text-sm transition ${ active ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-neutral-900 hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800" }`}
                              >
                                Class {cls.className}
                              </button>
                            );
                          })}
                        </div>
                        {row.classIds.length === 0 && (
                          <div className="text-xs text-amber-600 mt-2">
                            Not assigned to any class — this fee won&apos;t
                            apply anywhere.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="border rounded-md divide-y overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-neutral-50 dark:bg-neutral-900 dark:bg-neutral-950 text-xs text-neutral-500 dark:text-neutral-400">
                          <span>
                            Toggle class on/off, then set amount for each
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleSelectAllForRow(h._id)}
                            className="text-blue-600 hover:underline"
                          >
                            {allSelected ? "Clear all" : "Select all classes"}
                          </button>
                        </div>
                        {classes.map((cls) => {
                          const active = row.classIds
                            .map(String)
                            .includes(String(cls._id));
                          return (
                            <div
                              key={cls._id}
                              className={`flex flex-wrap items-center gap-3 px-3 py-2 ${
                                active ? "bg-white dark:bg-neutral-900" : "bg-neutral-50 dark:bg-neutral-900/60"
                              }`}
                            >
                              <label className="inline-flex items-center gap-2 min-w-[140px] cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={() =>
                                    toggleClassForRow(h._id, cls._id)
                                  }
                                  className="rounded border-gray-300 dark:border-neutral-700"
                                />
                                <span className="text-sm font-medium">
                                  Class {cls.className}
                                </span>
                              </label>
                              <div className="flex items-center gap-1 ml-auto">
                                <span className="text-xs text-neutral-400 dark:text-neutral-500">₹</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  disabled={!active}
                                  value={
                                    active
                                      ? row.perClass?.[cls._id] ?? ""
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setPerClassAmount(
                                      h._id,
                                      cls._id,
                                      e.target.value
                                    )
                                  }
                                  className="w-28 border rounded px-2 py-1 text-right text-sm disabled:bg-neutral-100 dark:bg-neutral-800 disabled:text-neutral-400 dark:text-neutral-500"
                                  placeholder={active ? "0.00" : "—"}
                                />
                              </div>
                            </div>
                          );
                        })}
                        {row.classIds.length === 0 && (
                          <div className="px-3 py-2 text-xs text-amber-600">
                            Select at least one class and enter its amount.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {feeHeads.length === 0 && (
              <div className="p-6 text-center text-neutral-500 dark:text-neutral-400 border rounded-lg">
                No active fee heads (transport heads are managed separately).
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-md disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Fee Structures"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FeeStructurePage;
